import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { UUID } from 'crypto'
import { addDays, addMonths, addYears, format, parseISO, startOfDay } from 'date-fns'
import {
  getPauseStatus,
  pauseSubscription,
  resumeSubscription,
  cancelScheduledPause,
  PauseStatusResponse,
} from '@/api/subscriptionsAPI'

interface SubscriptionManagementProps {
  orgUUID: UUID
  token: string
  onError: (error: string) => void
  onSuccess: (message: string) => void
}

export default function SubscriptionManagement({
  orgUUID,
  token,
  onError,
  onSuccess,
}: SubscriptionManagementProps) {
  const [pauseStatus, setPauseStatus] = useState<PauseStatusResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [pauseStartDate, setPauseStartDate] = useState('')
  const [pauseEndDate, setPauseEndDate] = useState('')
  const [pauseReason, setPauseReason] = useState('')
  const [showPauseDialog, setShowPauseDialog] = useState(false)
  const [pauseError, setPauseError] = useState<string | null>(null)
  const pauseErrorRef = useRef<HTMLDivElement>(null)
  const [showResumeDialog, setShowResumeDialog] = useState(false)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [showCancelScheduledPauseDialog, setShowCancelScheduledPauseDialog] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  // Calculate minimum and maximum pause start dates
  // Start date: today to 12 months from now
  const today = new Date()
  const minStartDate = new Date(today)
  const maxStartDate = addYears(today, 1)

  // Calculate minimum and maximum pause end dates (relative to start date)
  // End date: 1 month to 90 days from start date. addMonths (not setMonth)
  // matches the backend's relativedelta(months=1) at month boundaries —
  // e.g. Jan 31 + 1 month clamps to Feb 28 on both sides, rather than
  // overflowing to Mar 3 the way native Date.setMonth() would.
  const getEndDateConstraints = (startDateStr: string) => {
    // startOfDay, not `today` directly — `today` carries the current
    // time-of-day, so minEnd/maxEnd would too, and selecting exactly the
    // *displayed* (date-only) minimum would fail the `parsedEndDate <
    // minEndDate` check in handlePauseSubscription below.
    const startDate = startDateStr ? parseISO(startDateStr) : startOfDay(today)
    const minEnd = addMonths(startDate, 1)
    const maxEnd = addDays(startDate, 90)
    return { minEnd, maxEnd }
  }

  const { minEnd: minEndDate, maxEnd: maxEndDate } = getEndDateConstraints(pauseStartDate)
  const defaultStartDate = new Date(today) // Default to today (immediate pause)
  const defaultEndDate = addMonths(defaultStartDate, 1) // Default to 1 month from start

  const formatDate = (date: Date) => format(date, 'yyyy-MM-dd')

  useEffect(() => {
    fetchPauseStatus()
  }, [orgUUID, token])

  useEffect(() => {
    if (pauseError && pauseErrorRef.current) {
      pauseErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [pauseError])

  // `silent` is used for the refresh that follows a successful pause/resume/
  // cancel: that action already got its own onSuccess message, so a failure
  // here (a stale status display) must not call onError and wipe it out —
  // that would make a successful mutation look like it failed.
  const fetchPauseStatus = async (silent = false) => {
    try {
      setIsLoading(true)
      const status = await getPauseStatus(orgUUID, token)
      setPauseStatus(status)
    } catch (error) {
      if (silent) {
        console.error('Failed to refresh pause status after a successful action:', error)
      } else {
        onError(
          error instanceof Error ? error.message : 'Failed to fetch pause status'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handlePauseSubscription = async () => {
    // The start date field's HTML min/max are hints only — a manually typed
    // value isn't blocked by them, so validate before calling the API (same
    // gap the resume-date fix below already closes for that field).
    if (pauseStartDate) {
      if (pauseStartDate < formatDate(minStartDate)) {
        setPauseError('Pause start date cannot be in the past')
        return
      }
      if (pauseStartDate > formatDate(maxStartDate)) {
        setPauseError('Pause start date cannot be more than 12 months in the future')
        return
      }
    }

    const parsedEndDate = parseISO(pauseEndDate)
    if (parsedEndDate < minEndDate) {
      setPauseError('Minimum pause duration is 1 month')
      return
    }
    if (parsedEndDate > maxEndDate) {
      setPauseError('Maximum pause duration is 90 days')
      return
    }

    try {
      setIsLoading(true)
      setPauseError(null)
      const isScheduled = pauseStartDate && pauseStartDate !== formatDate(today)
      await pauseSubscription(
        {
          org_uuid: orgUUID,
          pause_end_date: pauseEndDate,
          pause_start_date: pauseStartDate || undefined,
          pause_reason: pauseReason || undefined,
        },
        token
      )
      onSuccess(
        isScheduled
          ? `Subscription pause scheduled for ${parseISO(pauseStartDate).toLocaleDateString()}`
          : 'Subscription paused successfully'
      )
      setShowPauseDialog(false)
      setPauseStartDate('')
      setPauseEndDate('')
      setPauseReason('')
      await fetchPauseStatus(true)
    } catch (error) {
      setPauseError(
        error instanceof Error
          ? error.message
          : 'Failed to pause subscription'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleResumeSubscription = async () => {
    try {
      setIsLoading(true)
      setResumeError(null)
      await resumeSubscription({ org_uuid: orgUUID }, token)
      onSuccess('Subscription resumed successfully')
      setShowResumeDialog(false)
      await fetchPauseStatus(true)
    } catch (error) {
      setResumeError(
        error instanceof Error
          ? error.message
          : 'Failed to resume subscription'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelScheduledPause = async () => {
    try {
      setIsLoading(true)
      setCancelError(null)
      await cancelScheduledPause(orgUUID, token)
      onSuccess('Scheduled pause has been cancelled')
      setShowCancelScheduledPauseDialog(false)
      await fetchPauseStatus(true)
    } catch (error) {
      setCancelError(
        error instanceof Error
          ? error.message
          : 'Failed to cancel scheduled pause'
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && !pauseStatus) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (!pauseStatus) {
    return null
  }

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {pauseStatus.currently_paused ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Your subscription is currently paused
              </Alert>

              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Pause Start Date:</strong>{' '}
                {pauseStatus.pause_start_date
                  ? parseISO(pauseStatus.pause_start_date).toLocaleDateString()
                  : 'N/A'}
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Scheduled Resume Date:</strong>{' '}
                {pauseStatus.pause_end_date
                  ? parseISO(pauseStatus.pause_end_date).toLocaleDateString()
                  : 'N/A'}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Days Remaining:</strong>{' '}
                {pauseStatus.days_remaining ?? 'N/A'}
              </Typography>

              <Button
                variant="contained"
                onClick={() => {
                  setResumeError(null)
                  setShowResumeDialog(true)
                }}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: '#1C3045',
                }}
              >
                Resume Subscription Early
              </Button>
            </Box>
          ) : pauseStatus.has_scheduled_pause ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                You have a scheduled pause starting on{' '}
                {pauseStatus.scheduled_pause_start_date
                  ? parseISO(pauseStatus.scheduled_pause_start_date).toLocaleDateString()
                  : 'N/A'}
              </Alert>

              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Pause Start Date:</strong>{' '}
                {pauseStatus.scheduled_pause_start_date
                  ? parseISO(pauseStatus.scheduled_pause_start_date).toLocaleDateString()
                  : 'N/A'}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Scheduled Resume Date:</strong>{' '}
                {pauseStatus.scheduled_pause_end_date
                  ? parseISO(pauseStatus.scheduled_pause_end_date).toLocaleDateString()
                  : 'N/A'}
              </Typography>

              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setCancelError(null)
                  setShowCancelScheduledPauseDialog(true)
                }}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Cancel Scheduled Pause
              </Button>
            </Box>
          ) : (
            <Box>
              <Alert
                severity={pauseStatus.can_pause ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                {pauseStatus.can_pause
                  ? 'You can pause your subscription once per calendar year'
                  : `You have already used your pause for this year (${pauseStatus.pauses_used_this_year}/${pauseStatus.pause_limit})`}
              </Alert>

              <Typography variant="body1" sx={{ mb: 3 }}>
                Pausing your subscription will stop billing temporarily. You can
                pause for up to 90 days, once per calendar year.
              </Typography>

              <Button
                variant="contained"
                onClick={() => {
                  setPauseStartDate(formatDate(defaultStartDate))
                  setPauseEndDate(formatDate(defaultEndDate))
                  setPauseReason('')
                  setPauseError(null)
                  setShowPauseDialog(true)
                }}
                disabled={!pauseStatus.can_pause || isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: '#1C3045',
                }}
              >
                Pause Subscription
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Pause Subscription Dialog */}
      <Dialog
        open={showPauseDialog}
        onClose={() => {
          if (isLoading) return
          setShowPauseDialog(false)
          setPauseError(null)
          setPauseReason('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Choose when your pause should start and when it should automatically
            resume. You can also resume early at any time.
          </Typography>

          {pauseError && (
            <Alert ref={pauseErrorRef} severity="error" sx={{ mb: 2 }}>
              {pauseError}
            </Alert>
          )}

          <TextField
            label="Pause Start Date"
            type="date"
            fullWidth
            value={pauseStartDate}
            onChange={(e) => {
              setPauseStartDate(e.target.value)
              // Recalculate end date constraints when start date changes
              const { minEnd } = getEndDateConstraints(e.target.value)
              if (pauseEndDate && parseISO(pauseEndDate) < minEnd) {
                setPauseEndDate(formatDate(minEnd))
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: formatDate(minStartDate),
              max: formatDate(maxStartDate),
            }}
            helperText="Leave as today for immediate pause, or select a future date to schedule"
            sx={{ mb: 2 }}
          />

          <TextField
            label="Resume Date"
            type="date"
            fullWidth
            value={pauseEndDate}
            onChange={(e) => setPauseEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: formatDate(minEndDate),
              max: formatDate(maxEndDate),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Reason (Optional)"
            multiline
            rows={3}
            fullWidth
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            placeholder="e.g., Summer break, temporary closure, etc."
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            You can pause for a minimum of 1 month and a maximum of 90 days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPauseDialog(false)
              setPauseError(null)
              setPauseReason('')
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePauseSubscription}
            variant="contained"
            disabled={isLoading || !pauseEndDate}
          >
            {isLoading ? 'Pausing...' : 'Pause Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Subscription Dialog */}
      <Dialog
        open={showResumeDialog}
        onClose={() => {
          if (isLoading) return
          setShowResumeDialog(false)
          setResumeError(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resume Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to resume your subscription now? Your billing
            will restart immediately.
          </Typography>

          {resumeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {resumeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowResumeDialog(false)
              setResumeError(null)
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResumeSubscription}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Resuming...' : 'Resume Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Scheduled Pause Dialog */}
      <Dialog
        open={showCancelScheduledPauseDialog}
        onClose={() => {
          if (isLoading) return
          setShowCancelScheduledPauseDialog(false)
          setCancelError(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Scheduled Pause</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to cancel your scheduled pause? Your
            subscription will continue without interruption, and you will still
            be able to schedule a pause within this calendar year.
          </Typography>

          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCancelScheduledPauseDialog(false)
              setCancelError(null)
            }}
            disabled={isLoading}
          >
            Keep Scheduled Pause
          </Button>
          <Button
            onClick={handleCancelScheduledPause}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Scheduled Pause'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
